import type { IconFC } from "@/lib/studio-tools";

// --- Resource Icons ---
const IconPdf: IconFC = ({ className }) => (
	<svg
		width="23"
		height="23"
		viewBox="0 0 24 24"
		fill="none"
		className={className}
	>
		<path
			d="M6 2a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6H6z"
			stroke="currentColor"
			strokeWidth="1.5"
			fill="none"
		/>
		<path
			d="M14 2v6h6"
			stroke="currentColor"
			strokeWidth="1.5"
			strokeLinecap="round"
			strokeLinejoin="round"
		/>
		<text
			x="6"
			y="18"
			fill="currentColor"
			fontSize="7"
			fontWeight="bold"
			fontFamily="Arial"
		>
			PDF
		</text>
	</svg>
);

const IconImage: IconFC = ({ className }) => (
	<svg
		width="23"
		height="23"
		viewBox="0 0 24 24"
		fill="none"
		className={className}
	>
		<rect
			x="3"
			y="4"
			width="18"
			height="16"
			rx="2"
			stroke="currentColor"
			strokeWidth="1.5"
		/>
		<circle cx="8.5" cy="9.5" r="1.5" fill="currentColor" />
		<path
			d="M4 17l4.5-4.5a1.5 1.5 0 012 0L15 16m0 0l2-2a1.5 1.5 0 012 0l1 1"
			stroke="currentColor"
			strokeWidth="1.5"
			strokeLinecap="round"
			strokeLinejoin="round"
		/>
	</svg>
);

const IconVideo: IconFC = ({ className }) => (
	<svg
		width="23"
		height="23"
		viewBox="0 0 24 24"
		fill="none"
		className={className}
	>
		<rect
			x="3"
			y="5"
			width="13"
			height="14"
			rx="2"
			stroke="currentColor"
			strokeWidth="1.5"
		/>
		<path
			d="M16 10l4.2-2.4a.6.6 0 01.9.5v7.8a.6.6 0 01-.9.5L16 14v-4z"
			stroke="currentColor"
			strokeWidth="1.5"
			strokeLinejoin="round"
		/>
	</svg>
);

export type PreviewKind = "image" | "video" | "other";

/**
 * The icon for a file, chosen by what it actually is.
 *
 * Everything used to draw the PDF sheet regardless of format, which made a
 * video and a spreadsheet indistinguishable in the list.
 */
const EXTENSION_KIND: Record<string, "image" | "video"> = {
	jpg: "image",
	jpeg: "image",
	png: "image",
	gif: "image",
	webp: "image",
	svg: "image",
	mp4: "video",
	mov: "video",
	webm: "video",
};

/**
 * How to render a file, from its mime or — failing that — its name.
 *
 * Rows written before mime_type existed carry no mime at all, and without the
 * fallback every one of them lands in the iframe branch, where an image is
 * shown at full size instead of being scaled to fit.
 */
export function previewKind(
	mimeType: string | null,
	name?: string | null,
): PreviewKind {
	if (mimeType?.startsWith("image/")) return "image";
	if (mimeType?.startsWith("video/")) return "video";

	const ext = name?.includes(".")
		? name.split(".").pop()?.toLowerCase()
		: undefined;
	return (ext && EXTENSION_KIND[ext]) || "other";
}

export function iconForMime(
	mimeType: string | null,
	name?: string | null,
): IconFC {
	const kind = previewKind(mimeType, name);
	if (kind === "image") return IconImage;
	if (kind === "video") return IconVideo;
	return IconPdf;
}
